import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

export default function AdminLogin() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const { admin, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin && admin.email) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t.admin.loginTitle + " ✓");
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error || "Error");
    }
  };

  const inputCls =
    "rounded-none bg-mtk-surface border border-mtk-border text-white placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-mtk-gold focus-visible:ring-offset-0 focus-visible:border-mtk-gold/60 h-12";
  const labelCls = "font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400";

  return (
    <div className="min-h-screen bg-mtk-bg flex flex-col" data-testid="admin-login-page">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-mtk-border">
        <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-mtk-gold transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase">HOME</span>
        </Link>
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-lg font-black tracking-[0.2em] text-white">MTK</span>
          <span className="font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold uppercase">ADMIN</span>
        </div>
        <button
          onClick={toggle}
          data-testid="login-language-toggle"
          className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-mtk-gold transition-colors border border-white/10 hover:border-mtk-gold/40 px-3 py-2"
        >
          <span className={lang === "es" ? "text-mtk-gold" : ""}>ES</span>
          <span className="mx-1.5 text-white/20">/</span>
          <span className={lang === "en" ? "text-mtk-gold" : ""}>EN</span>
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="border border-mtk-border bg-mtk-surface p-8 md:p-12">
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-4 h-4 text-mtk-gold" />
              <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">SECURE</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
              {t.admin.loginTitle}
            </h1>
            <p className="text-zinc-400 mt-2 text-sm">{t.admin.loginSubtitle}</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className={labelCls}>{t.admin.email}</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="admin@mtksystem.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className={labelCls}>{t.admin.password}</Label>
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="••••••••"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-sm h-12 disabled:opacity-60"
              >
                {loading ? t.admin.loggingIn : t.admin.login}
              </Button>
            </form>
          </div>
          <p className="mt-6 text-center font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-600">
            MTK SYSTEM · INTERNAL
          </p>
        </div>
      </main>
    </div>
  );
}
